/* eslint-disable import/no-anonymous-default-export */

import { useState, useContext, useEffect } from 'react';
import { formatListData, AjaxContext, AppContext } from 'commons-ui';
import { IPaymentNotificationTemplate, SDFilters, defaultSDFilter, getAllPaymentGatewayListUri, listAPIUri } from './constants';

const useService = () => {
	const { postData, getData } = useContext(AjaxContext);
	const {
		showAlert,
		app: { appName }
	} = useContext(AppContext);
	const [showLoader, setShowLoader] = useState<boolean>(false);
	const [forceUpdate, setForceUpdate] = useState<boolean>(false);
	const [pageNumber, setPageNumber] = useState<any>({ currPage: 0, nextPage: null });
	const [downtimeData, setDowntimeData] = useState<any>({ data: [], rowCount: 0 });
	const [paymentGatewayList, setPaymentGatewayList] = useState<any>([]);
	const [filters, setFilters] = useState<SDFilters | undefined>(defaultSDFilter);

	const getDowntimeData = async (jumpPageNumber?: number) => {
		try {
			if (!filters) return;
			const nxtPage: number = jumpPageNumber || 0;
			setShowLoader(true);
			const response = await postData({
				endpoint: listAPIUri,
				body: {
					page: {
						pageSize: 15,
						pageNo: nxtPage
					},
					...filters
				},
				queryParams: {
					appName
				}
			});
			if (response?.error) {
				showAlert(`Error while getting scheduled downtime data! Error: ${response?.data?.error}`, { type: 'error' });
				return;
			}
			const listData: IPaymentNotificationTemplate[] = formatListData(response?.data?.data);
			listData.forEach((data: IPaymentNotificationTemplate, index: number) => {
				if (data?.extra) {
					listData[index].delink = data?.extra?.delink;
					listData[index].isPGError = data?.extra?.isPGError;
					listData[index].instantFailure = data?.extra?.instantFailure || false;
				}
			});
			const nextOffset: number | null =
				response?.data?.data?.length > 0 && response?.data?.data?.length >= 0 ? nxtPage + 1 : null;
			setDowntimeData({ rowCount: response?.data?.data?.length, data: listData });
			setPageNumber({ currPage: nxtPage, nextPage: nextOffset });
			setForceUpdate(true);
		} catch (error: any) {
			setDowntimeData({ data: [], rowCount: 0 });
			setPageNumber({ currPage: 0, nextPage: null });
			setForceUpdate(true);
			showAlert(`Error while getting scheduled downtime data! Error: ${error?.response?.data?.error}`, { type: 'error' });
		} finally {
			setShowLoader(false);
		}
	};

	const getAllPaymentGatewayList = async () => {
		const response = await getData({
			endpoint: getAllPaymentGatewayListUri,
			queryParams: {
				appName
			}
		});
		setPaymentGatewayList(response?.data?.data);
		return response?.data?.data;
	};

	const jumpBack = async () => {
		if (pageNumber.currPage > 0) {
			await getDowntimeData(pageNumber.currPage - 1);
		}
	};

	const jumpNext = async () => {
		if (pageNumber.nextPage !== null) {
			await getDowntimeData(pageNumber.nextPage);
		}
	};

	const getAllDowntimeData = () => {
		setForceUpdate(false);
		return downtimeData;
	};

	useEffect(() => {
		getAllPaymentGatewayList();
		getDowntimeData(0);
	}, [filters]);

	const handleFormSubmit = async (filterData: SDFilters) => {
		try {
			setFilters(filterData);
			return;
		} catch (error) {
			showAlert('Error in fetching invoice data', { type: 'error' });
		}
	};

	return {
		getDowntimeData,
		showLoader,
		setShowLoader,
		forceUpdate,
		jumpBack,
		jumpNext,
		pageNumber,
		getAllDowntimeData,
		setForceUpdate,
		handleFormSubmit,
		filters,
		paymentGatewayList
	};
};

export default useService;
